import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Minus, ShoppingBag, Search, Zap } from "lucide-react";
import { EmptyState } from "@/components/common";
import { api, inr, apiError } from "@/lib/api";

export default function Quick() {
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [cart, setCart] = useState({});
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("asap");
  const [checkout, setCheckout] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/quick/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/quick/slots").then((r) => setSlots(r.data)).catch(() => {});
    api.get("/addresses").then((r) => setAddresses(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setTimeout(() => { const p = {}; if (cat) p.category = cat; if (q) p.q = q; api.get("/quick/products", { params: p }).then((r) => setProducts(r.data)).catch(() => {}); }, 250);
    return () => clearTimeout(t);
  }, [cat, q]);

  const pmap = Object.fromEntries(products.map((p) => [p.id, p]));
  const add = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const sub = (id) => setCart((c) => { const n = { ...c }; n[id] = (n[id] || 0) - 1; if (n[id] <= 0) delete n[id]; return n; });
  const count = Object.values(cart).reduce((a, b) => a + b, 0);
  const subtotal = Object.entries(cart).reduce((s, [id, qty]) => s + (pmap[id]?.price || 0) * qty, 0);

  const placeOrder = async () => {
    if (!addresses.length) { toast.error("Add a delivery address first"); nav("/addresses"); return; }
    setBusy(true);
    try {
      const items = Object.entries(cart).map(([product_id, qty]) => ({ product_id, qty }));
      const { data } = await api.post("/quick/order", { items, address_id: addresses[0].id, slot, payment_method: "cod" });
      toast.success("BiteGoFood Quick order placed!");
      setCart({}); setCheckout(false); nav(`/order/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-surface max-w-[520px] mx-auto pb-32">
      <div className="sticky top-0 z-40 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-3 pt-3 pb-3 rounded-b-3xl">
        <div className="flex items-center gap-2">
          <button onClick={() => nav("/")} className="w-9 h-9 flex items-center justify-center tap"><ArrowLeft size={22} /></button>
          <div className="flex-1"><div className="font-display font-extrabold text-lg flex items-center gap-1"><Zap size={18} /> BiteGoFood Quick</div><div className="text-[11px] text-white/85">Groceries in minutes</div></div>
        </div>
        <div className="mt-2 flex items-center gap-2 bg-white rounded-xl px-3 py-2.5">
          <Search size={18} className="text-emerald-600" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search milk, bread, eggs…" className="flex-1 min-w-0 bg-transparent text-sm text-ink outline-none" data-testid="quick-search" />
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto hide-scrollbar px-4 py-3">
        <button onClick={() => setCat("")} className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border ${!cat ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-border text-ink-600"}`}>All</button>
        {cats.map((c) => <button key={c.id} onClick={() => setCat(c.name)} className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border ${cat === c.name ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-border text-ink-600"}`} data-testid={`quick-cat-${c.id}`}>{c.name}</button>)}
      </div>

      <div className="px-4 grid grid-cols-2 gap-3">
        {products.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-border p-2.5" data-testid={`quick-product-${p.id}`}>
            <img src={p.image} alt={p.name} loading="lazy" className="w-full h-24 object-cover rounded-xl" />
            <div className="font-bold text-sm text-ink mt-2 leading-tight">{p.name}</div>
            <div className="text-[11px] text-ink-600">{p.unit} · {p.stock > 0 ? `${p.stock} in stock` : "Out of stock"}</div>
            <div className="flex items-center justify-between mt-1.5">
              <div><span className="font-bold text-sm">{inr(p.price)}</span>{p.mrp > p.price && <span className="text-[11px] text-ink-600 line-through ml-1">{inr(p.mrp)}</span>}</div>
              {cart[p.id] ? (
                <div className="flex items-center bg-emerald-600 text-white rounded-lg"><button onClick={() => sub(p.id)} className="w-7 h-7 flex items-center justify-center"><Minus size={14} /></button><span className="w-5 text-center text-sm font-bold">{cart[p.id]}</span><button onClick={() => add(p.id)} className="w-7 h-7 flex items-center justify-center"><Plus size={14} /></button></div>
              ) : (
                <button onClick={() => add(p.id)} disabled={p.stock <= 0} className="border border-emerald-600 text-emerald-600 font-bold text-xs px-3 py-1.5 rounded-lg disabled:opacity-40" data-testid={`quick-add-${p.id}`}>ADD</button>
              )}
            </div>
          </div>
        ))}
      </div>
      {products.length === 0 && <EmptyState icon={ShoppingBag} title="No products found" subtitle="Try another category or search." />}

      {count > 0 && !checkout && (
        <button onClick={() => setCheckout(true)} className="fixed bottom-5 left-4 right-4 max-w-[488px] mx-auto z-40 bg-emerald-600 text-white rounded-2xl px-4 py-3.5 flex items-center justify-between shadow-lg tap" data-testid="quick-checkout-bar">
          <span className="font-bold">{count} item{count > 1 ? "s" : ""} · {inr(subtotal)}</span><span className="font-bold">Checkout →</span>
        </button>
      )}
      {checkout && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end" onClick={() => setCheckout(false)}>
          <div className="bg-white w-full max-w-[520px] mx-auto rounded-t-3xl p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg text-ink">Delivery slot</h3>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {slots.map((s) => <button key={s.key} onClick={() => setSlot(s.key)} className={`rounded-xl p-2.5 text-left border text-sm font-semibold ${slot === s.key ? "border-emerald-600 bg-emerald-50" : "border-border"}`} data-testid={`quick-slot-${s.key}`}>{s.label}<div className="text-[11px] text-ink-600 font-normal">{s.fee ? `${inr(s.fee)} fee` : "Free"}</div></button>)}
            </div>
            <div className="flex justify-between mt-4 font-bold"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
            <p className="text-[11px] text-ink-600">Delivering to {addresses[0]?.line1 || "— add an address"} · Cash on Delivery</p>
            <button onClick={placeOrder} disabled={busy} className="w-full bg-emerald-600 text-white font-bold py-3.5 rounded-xl mt-3 tap" data-testid="quick-place-order">{busy ? "Placing…" : `Place Order · ${inr(subtotal)}`}</button>
          </div>
        </div>
      )}
    </div>
  );
}
